import { ChangeEvent, useState } from 'react';

import {
  AddIcon,
  CommonZoomInIcon,
  DeleteIcon,
  Divider,
  IconBoxButton,
  Label,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Text,
  TextButton,
  type TypeSelectOption,
  dialogService,
} from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { Bullet, NumberUnitInput } from '~/components/common';

import {
  useGetOrderTypeInfoQuery,
  usePostSkuImageDeleteMutation,
  usePostSkuImageMutation,
  useProductModalOptions,
} from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';
import { validateSingleProductField } from '~/utils';

import { FILE_SIZE_LIMIT, TOAST_MESSAGE } from '~/constants';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductRegisterModalStockInfoSectionDetails.module.scss';

export const ProductRegisterModalStockInfoSectionDetails = () => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const productModalOptions = useProductModalOptions();

  const [
    productRegisterModalValue,
    setProductRegisterModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.setProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  const orderTypeInfoQuery = useGetOrderTypeInfoQuery({ showGlobalErrorToast: false });

  const uploadSkuImageMutation = usePostSkuImageMutation();
  const deleteSkuImageMutation = usePostSkuImageDeleteMutation();

  const [openImagePreviewModal, setOpenImagePreviewModal] = useState(false);

  const handleClickRequiredInput = (id: string) => {
    resetProductModalRequiredValueStatus(id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeIsNeedCosmeticPackingRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      isNeedCosmeticPacking: e.target.value === 'true',
    });

    resetProductModalRequiredValueStatus('isNeedCosmeticPacking');
  };

  const handleChangeKurlyOrderTypeSelectChange = (_: unknown, value: TypeSelectOption<string>) => {
    if (!orderTypeInfoQuery.data?.data) {
      setProductRegisterModalValue({
        ...productRegisterModalValue,
        kurlyOrderType: value,
        parcelOrderType: null,
        massDocumentNum: null,
        kurlyKeepType: null,
      });

      return;
    }

    const orderTypeInfo = orderTypeInfoQuery.data.data[value.value];

    if (!orderTypeInfo) {
      setProductRegisterModalValue({
        ...productRegisterModalValue,
        kurlyOrderType: value,
        parcelOrderType: null,
        massDocumentNum: null,
        kurlyKeepType: null,
      });
      return;
    }

    const { kurlyKeepType, massDocumentNum, parcelOrderType } = orderTypeInfo;

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      kurlyOrderType: value,
      parcelOrderType: productModalOptions?.deliveryDocumentNum.find(({ value }) => value === parcelOrderType) ?? null,
      massDocumentNum: productModalOptions?.massDocumentNum.find(({ value }) => value === massDocumentNum) ?? null,
      kurlyKeepType: productModalOptions?.kurlyKeepType.find(({ value }) => value === kurlyKeepType) ?? null,
    });

    resetProductModalRequiredValueStatus(['kurlyOrderType', 'parcelOrderType', 'massDocumentNum']);
  };

  const handleChangeParcelOrderTypeSelectChange = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      parcelOrderType: value,
    });

    resetProductModalRequiredValueStatus('parcelOrderType');
  };

  const handleChangeMassDocumentNumSelectChange = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      massDocumentNum: value,
    });

    resetProductModalRequiredValueStatus('massDocumentNum');
  };

  const handleChangeIsInspectionRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      isInspection: e.target.value === 'true',
    });

    resetProductModalRequiredValueStatus('isInspection');
  };

  const handleChangeSafeStockDayInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : e.target.value;

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      stockSafeDate: value,
    });

    const validationResult = validateSingleProductField('stockSafeDate', value);

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      stockSafeDate: validationResult,
    });
  };

  const handleChangeDeliveryDocumentNumSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      deliveryDocumentNum: value,
    });
  };

  const handleChangePackingDirectionSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      packingDirection: value,
    });

    resetProductModalRequiredValueStatus('packingDirection');
  };

  const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.persist();

    const image = e.target.files?.[0];

    if (!image) {
      dialogService.addDialog({
        children: '이미지 업로드에 실패했습니다. 다시 시도해주세요.',
      });
      return;
    }

    const { size, type } = image;

    if (FILE_SIZE_LIMIT < size) {
      dialogService.addDialog({
        children: '10MB 이하 이미지만 첨부할 수 있습니다.',
      });
      return;
    }

    const availableType = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!availableType.includes(type)) {
      dialogService.addDialog({
        children: '허용하지 않는 파일 타입입니다.',
      });
      return;
    }

    uploadSkuImageMutation.mutate(image, {
      onSuccess: (data) => {
        setProductRegisterModalValue({
          ...productRegisterModalValue,
          skuImageUrl: data.skuImageUrl ?? '',
        });
      },
      onError: (error) => {
        dialogService.addDialog({
          children: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      },
    });
  };

  const handleClickDeleteImage = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: '이미지를 삭제하시겠습니까?',
      cancelText: '취소',
      confirmText: '삭제',
      onConfirm: deleteImage,
    });
  };

  const deleteImage = () => {
    deleteSkuImageMutation.mutate(productRegisterModalValue.skuImageUrl, {
      onSuccess: () => {
        setProductRegisterModalValue({
          ...productRegisterModalValue,
          skuImageUrl: '',
        });
      },
      onError: (error) => {
        dialogService.addDialog({
          children: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      },
    });
  };

  const handleClickZoomButton = () => {
    setOpenImagePreviewModal(true);
  };

  const handleClickConfirmPreviewModal = () => {
    setOpenImagePreviewModal(false);
  };

  return (
    <>
      <div ref={(el) => setFocusFieldRefs('isNeedCosmeticPacking', el)} className={styles['item-row']}>
        <Label className={styles['label']} title="뷰티포장" required={hasPermission3PL} />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="is-need-cosmetic-packing"
            value={String(productRegisterModalValue.isNeedCosmeticPacking)}
            size="medium"
            status={productModalRequiredValueStatus.isNeedCosmeticPacking?.status}
            validationMessage={productModalRequiredValueStatus.isNeedCosmeticPacking?.message}
            onChange={handleChangeIsNeedCosmeticPackingRadio}
          >
            <Radio value="true" label="뷰티포장 필요" />
            <Radio value="false" label="뷰티포장 없음" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="오더 유형" required />
        <div className={styles['order-type']}>
          <div className={styles['order-type-wrapper']}>
            <Text className={styles['title']} size="large">
              샛별
            </Text>
            <Select
              ref={(el) => setFocusFieldRefs('kurlyOrderType', el)}
              id="kurlyOrderType"
              className={styles['select']}
              size="small"
              options={productModalOptions?.deliveryType ?? []}
              value={productRegisterModalValue.kurlyOrderType}
              placeholder="선택"
              status={productModalRequiredValueStatus.kurlyOrderType.status}
              validationMessage={productModalRequiredValueStatus.kurlyOrderType.message}
              onClick={() => handleClickRequiredInput('kurlyOrderType')}
              onChange={handleChangeKurlyOrderTypeSelectChange}
            />
          </div>
          <Divider orientation="vertical" className={styles['divider']} />
          <div className={styles['order-type-wrapper']}>
            <Text className={styles['title']} size="large">
              택배
            </Text>
            <Select
              ref={(el) => setFocusFieldRefs('parcelOrderType', el)}
              id="parcelOrderType"
              className={styles['select']}
              size="small"
              options={productModalOptions?.deliveryDocumentNum ?? []}
              value={productRegisterModalValue.parcelOrderType}
              placeholder="선택"
              status={productModalRequiredValueStatus.parcelOrderType.status}
              validationMessage={productModalRequiredValueStatus.parcelOrderType.message}
              onClick={() => handleClickRequiredInput('parcelOrderType')}
              onChange={handleChangeParcelOrderTypeSelectChange}
            />
          </div>
          <Divider orientation="vertical" className={styles['divider']} />
          <div className={styles['order-type-wrapper']}>
            <Text className={styles['title']} size="large">
              대량
            </Text>
            <Select
              ref={(el) => setFocusFieldRefs('massDocumentNum', el)}
              id="massDocumentNum"
              className={styles['select']}
              size="small"
              options={productModalOptions?.massDocumentNum ?? []}
              value={productRegisterModalValue.massDocumentNum}
              placeholder="선택"
              status={productModalRequiredValueStatus.massDocumentNum.status}
              validationMessage={productModalRequiredValueStatus.massDocumentNum.message}
              onClick={() => handleClickRequiredInput('massDocumentNum')}
              onChange={handleChangeMassDocumentNumSelectChange}
            />
          </div>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="컬리 보관 유형" />
        <div className={styles['input-wrapper']}>
          <Select
            options={productModalOptions?.kurlyKeepType ?? []}
            size="small"
            value={productRegisterModalValue.kurlyKeepType}
            disabled
            placeholder="오더 유형 선택 시 자동 설정"
          />
        </div>
      </div>

      <div ref={(el) => setFocusFieldRefs('isInspection', el)} className={styles['item-row']}>
        <Label className={styles['label']} title="검품 대상 여부" required={hasPermission3PL} />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="is-inspection"
            value={String(productRegisterModalValue.isInspection)}
            size="medium"
            status={productModalRequiredValueStatus.isInspection?.status}
            validationMessage={productModalRequiredValueStatus.isInspection?.message}
            onChange={handleChangeIsInspectionRadio}
          >
            <Radio value="true" label="예" />
            <Radio value="false" label="아니오" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="안전재고일수" />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            ref={(el) => setFocusFieldRefs('stockSafeDate', el)}
            id="stockSafeDate"
            size="small"
            value={productRegisterModalValue.stockSafeDate ?? ''}
            unit="일"
            placeholder="숫자만 입력"
            status={productModalRequiredValueStatus.stockSafeDate?.status}
            validationMessage={productModalRequiredValueStatus.stockSafeDate?.message}
            onClick={() => handleClickRequiredInput('stockSafeDate')}
            onChange={handleChangeSafeStockDayInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="배송 배차 유형" />
        <div className={styles['input-wrapper']}>
          <Select
            id="deliveryDocumentNum"
            options={productModalOptions?.deliveryDispatchType ?? []}
            size="small"
            value={productRegisterModalValue.deliveryDocumentNum}
            placeholder="선택"
            onChange={handleChangeDeliveryDocumentNumSelect}
          />
        </div>
      </div>

      <div className={styles['input-image-row']}>
        <Label className={styles['label']} title="실물 이미지" />
        {productRegisterModalValue.skuImageUrl ? (
          <div className={styles['image-preview-wrapper']}>
            <div className={styles['image-preview']}>
              <IconBoxButton
                color="secondary"
                className={styles['icon-button']}
                size="tiny"
                onClick={handleClickZoomButton}
              >
                <CommonZoomInIcon size={16} />
              </IconBoxButton>
              <img src={productRegisterModalValue.skuImageUrl} alt="실물 이미지 미리보기" />
            </div>
            <div className={styles['image-actions']}>
              <TextButton status="error" size="tiny" onClick={handleClickDeleteImage}>
                <DeleteIcon size={16} color="#E22D2E" />
                삭제
              </TextButton>
            </div>
          </div>
        ) : (
          <div className={styles['image-upload-wrapper']}>
            <div>
              <label htmlFor="skuImageUrl">
                <input
                  id="skuImageUrl"
                  type="file"
                  className={styles['input']}
                  accept="image/jpg, image/JPG, image/jpeg, image/JPEG, image/png, image/PNG"
                  multiple={false}
                  onClick={() => handleClickRequiredInput('skuImageUrl')}
                  onChange={handleSelectFile}
                />
                <div className={styles['upload-label-box']}>
                  <AddIcon size={32} color="gray400" />
                  <Text color="quaternary" weight="semi-bold" size="small">
                    이미지 선택
                  </Text>
                </div>
              </label>
            </div>
            <div className={styles['text-wrapper']}>
              <Text color="quaternary" size="xsmall" className={styles['text']}>
                <Bullet fill="#BCC4CC" /> 최대 1개까지 등록할 수 있습니다. (10MB 미만)
              </Text>
              <Text color="quaternary" size="xsmall" className={styles['text']}>
                <Bullet fill="#BCC4CC" /> jpg, jpeg, png 확장자 파일만 업로드 가능합니다.
              </Text>
            </div>
          </div>
        )}
      </div>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="정방향여부" required={hasPermissionFBK} />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('packingDirection', el)}
            id="packingDirection"
            options={productModalOptions?.packingDirection ?? []}
            size="small"
            value={productRegisterModalValue.packingDirection}
            dropdownPlacement="top"
            status={productModalRequiredValueStatus.packingDirection?.status}
            validationMessage={productModalRequiredValueStatus.packingDirection?.message}
            onChange={handleChangePackingDirectionSelect}
          />
        </div>
      </div>
      <Modal
        title="미리보기"
        open={openImagePreviewModal}
        confirmText="확인"
        onConfirm={handleClickConfirmPreviewModal}
        size="small"
        depth={2}
      >
        <img src={productRegisterModalValue.skuImageUrl} alt="등록 이미지 미리보기" width="100%" />
      </Modal>
    </>
  );
};

export default ProductRegisterModalStockInfoSectionDetails;
